import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Pencil, Plus, Trash2 } from "lucide-react";
import { useState, type FormEvent } from "react";
import { Badge, Button, Card, ErrorText, Input, Label, Modal } from "@/components/ui";
import { api } from "@/lib/api";
import type { Group } from "@/lib/types";
import { useGroups } from "./Users";

export default function Groups() {
  const qc = useQueryClient();
  const groups = useGroups();
  const [editing, setEditing] = useState<Group | "new" | null>(null);
  const remove = useMutation({
    mutationFn: (id: string) => api.delete(`/api/admin/groups/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["admin"] }),
  });
  return (
    <Card
      title="Groups"
      actions={
        <Button size="sm" onClick={() => setEditing("new")}>
          <Plus size={16} /> Add group
        </Button>
      }
    >
      <p className="mb-3 text-sm text-zinc-500">Groups control access to models, MCP servers and shared workers.</p>
      {groups.data?.length === 0 && <p className="text-sm text-zinc-500">No groups yet.</p>}
      <ul className="divide-y divide-zinc-200 dark:divide-zinc-800">
        {groups.data?.map((g) => (
          <li key={g.id} className="flex items-center justify-between gap-4 py-2.5">
            <p className="flex items-center gap-2 text-sm font-medium">
              {g.name} {g.oidc_name && <Badge>OIDC: {g.oidc_name}</Badge>}
            </p>
            <div className="flex gap-1">
              <Button variant="ghost" size="icon" onClick={() => setEditing(g)} aria-label="Edit">
                <Pencil size={16} />
              </Button>
              <Button variant="ghost" size="icon" aria-label="Delete" onClick={() => confirm(`Delete group ${g.name}?`) && remove.mutate(g.id)}>
                <Trash2 size={16} />
              </Button>
            </div>
          </li>
        ))}
      </ul>
      {editing && <GroupForm group={editing === "new" ? null : editing} onClose={() => setEditing(null)} />}
    </Card>
  );
}

function GroupForm({ group, onClose }: { group: Group | null; onClose: () => void }) {
  const qc = useQueryClient();
  const [form, setForm] = useState({ name: group?.name ?? "", oidc_name: group?.oidc_name ?? "" });
  const save = useMutation({
    mutationFn: () => {
      const body = { name: form.name, oidc_name: form.oidc_name || null };
      return group ? api.patch(`/api/admin/groups/${group.id}`, body) : api.post("/api/admin/groups", body);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin"] });
      onClose();
    },
  });
  function submit(e: FormEvent) {
    e.preventDefault();
    save.mutate();
  }
  return (
    <Modal open onClose={onClose} title={group ? "Edit group" : "Add group"}>
      <form className="space-y-4" onSubmit={submit}>
        <Label label="Name">
          <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required />
        </Label>
        <Label label="OIDC group name" hint="Users whose OIDC group claim contains this value are added at login.">
          <Input value={form.oidc_name} onChange={(e) => setForm({ ...form, oidc_name: e.target.value })} />
        </Label>
        <ErrorText error={save.error} />
        <div className="flex justify-end gap-2">
          <Button type="button" variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button disabled={save.isPending}>Save</Button>
        </div>
      </form>
    </Modal>
  );
}
